import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FaFilter } from 'react-icons/fa'
import { FiInbox } from 'react-icons/fi'
import { VscClearAll } from 'react-icons/vsc'

/* CSS */

import '../../shared/components/form/inputDefault.css'

/* form */
import Input from "../../shared/components/form/input"

/* Hooks */
import useMyPermission from '../../hooks/useMyPermission'

import ListProductRow from './ListRow/ListProductRow'
import Modal from '../../shared/modal/Modal'
import NewProduct from './NewProduct'
import Button from '../../shared/components/Button/Button'
import ProductFilterModal from './ProductFilterModal'

const EMPTY_FILTERS = { sex: 'all', category: '', provider: '', cloth: '', sizeGrid: '' }

function Product({ handleOnSelected, itemData, enableSelectRow, onFinishSelect }) {

    const [token] = useState(localStorage.getItem('token') || '')
    const [products, setProducts] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [modalFilter, setModalFilter] = useState(false)
    const [filters, setFilters] = useState(EMPTY_FILTERS)

    const [search, setSearch] = useState("")
    const [load, setLoad] = useState(false)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: "", })
    const { checkPermission } = useMyPermission()
    const myPermissionNew = checkPermission(myPermission, "product_create")
    const myPermissionView = checkPermission(myPermission, "product_view")
    const myPermissionEdit = checkPermission(myPermission, "product_edit")
    const myPermissionDelete = checkPermission(myPermission, "product_delete")

    /* get all function */
    const getAll = () => {
        api.get(`/product/getall`, {
            headers: {
                Authorization: `Bearer ${JSON.parse(token)}`,
            },
        }).then((response) => {
            if (response) {
                setProducts(response.data.products)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    /* use effect */
    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    /* ----------------------- Search / filter function ---------------------------------- */
    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return products.filter((item) => {
            const matchSearch = item.description.toLowerCase().includes(searchLowerCase)
            const matchSex = filters.sex === 'all' || item.sex?.value === filters.sex
            const matchCategory = !filters.category || item.category?._id === filters.category
            const matchProvider = !filters.provider || item.provider?._id === filters.provider
            const matchCloth = !filters.cloth || item.cloth?._id === filters.cloth
            const matchSizeGrid = !filters.sizeGrid || item.sizeGrid?._id === filters.sizeGrid
            return matchSearch && matchSex && matchCategory && matchProvider && matchCloth && matchSizeGrid
        })
    }, [products, search, filters])

    function handleSearch(e) {
        setSearch(e.target.value)
    }

    const relationOptions = useMemo(() => {
        const toOptions = (list) => {
            const map = new Map()
            products.forEach((p) => { const v = p[list]; if (v && v._id) map.set(String(v._id), v.name) })
            return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
        }
        return {
            category: toOptions('category'),
            provider: toOptions('provider'),
            cloth: toOptions('cloth'),
            sizeGrid: toOptions('sizeGrid'),
        }
    }, [products])

    const hasActiveFilter = !!search || filters.sex !== 'all' || !!filters.category || !!filters.provider || !!filters.cloth || !!filters.sizeGrid

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando produtos...</div>
    }

    /* PAGE */
    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Produtos</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {onFinishSelect && (
                                <Button variant="finish" onClick={onFinishSelect}>Concluir</Button>
                            )}
                            {myPermissionNew && !onFinishSelect && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por descrição..."
                                    value={search}
                                    handleOnChange={handleSearch}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'produto' : 'produtos'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListProductRow
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermissionEdit={myPermissionEdit}
                        myPermissionDelete={myPermissionDelete}
                        myPermissionView={myPermissionView}
                        handleOnSelected={handleOnSelected}
                        itemData={itemData}
                        enableSelectRow={enableSelectRow}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum produto encontrado</p>
                        <p className="pg-empty-subtitle">
                            {products.length === 0
                                ? 'Cadastre o primeiro produto para começar.'
                                : 'Tente ajustar a busca.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                t={2}
                w={96}
                h={94}
                content={
                    <NewProduct btCancelAction={() => setModalNew(false)} handleOnSuccess={() => { setModalNew(false); getAll() }} />
                }
            />

            {modalFilter && (
                <ProductFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    categoryOptions={relationOptions.category}
                    providerOptions={relationOptions.provider}
                    clothOptions={relationOptions.cloth}
                    sizeGridOptions={relationOptions.sizeGrid}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar produtos</p>
        </div>
    )
}

export default Product
